import { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiCalendar, FiPlus, FiSearch } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import TicketModal from './TicketModal';
import classNames from 'utilities/ClassNames';
import './TicketsFilters.css';

interface TicketsFiltersProps {
    filters: {
        status: string;
        customer: string;
        assignedTo: string;
        startDate: Date | null;
        endDate: Date | null;
        statuses: string[];
        search: string;
        organization: string;
    };
    setFilters: (filters: any) => void;
}

export default function TicketsFilters({ filters, setFilters }: TicketsFiltersProps) {
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

    const statuses = [
        { id: 'open', label: 'Open' },
        { id: 'assigned', label: 'Assigned' },
        { id: 'inprogress', label: 'In Progress' },
        { id: 'resolved', label: 'Resolved' },
        { id: 'closed', label: 'Closed' }
    ];

    const customer = [
        { id: 'John Doe', label: 'John Doe' },
        { id: 'Donna May', label: 'Donna May' },
        { id: 'James Vance', label: 'James Vance' },
        { id: 'Stephen Ogden', label: 'Stephen Ogden' },
        { id: 'Stewart Skinner', label: 'Stewart Skinner' },
        { id: 'Mark Francis', label: 'Mark Francis' }
    ];

    const organizations = [
        { id: 'acme', label: 'Acme Corp' },
        { id: 'globex', label: 'Globex Inc' },
        { id: 'initech', label: 'Initech' },
        { id: 'umbrella', label: 'Umbrella Corp' }
    ];

    const assignees = [
        { id: 'unassigned', label: 'Unassigned' },
        { id: 'john_smith', label: 'John Smith' },
        { id: 'sarah_parker', label: 'Sarah Parker' },
        { id: 'mike_johnson', label: 'Mike Johnson' },
        { id: 'emily_brown', label: 'Emily Brown' },
        { id: 'david_wilson', label: 'David Wilson' },
    ];

    const handleDropdownClick = (dropdownName: string) => {
        setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    };

    const handleOptionSelect = (dropdownName: string, value: string, id: string) => {
        setFilters((prev: any) => ({ ...prev, [dropdownName]: id }));
        setOpenDropdown(null);
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            customer: '',
            assignedTo: '',
            startDate: null,
            endDate: null,
            statuses: [],
            search: '',
            organization: ''
        });
        setSelectedStatuses([]);
        setSearchTerm('');
    };

    const handleStatusButtonClick = (statusId: string) => {
        setSelectedStatuses(prev => {
            if (prev.includes(statusId)) {
                return prev.filter(id => id !== statusId);
            } else {
                return [...prev, statusId];
            }
        });
    };

    useEffect(() => {
        setFilters((prev: any) => ({
            ...prev,
            statuses: selectedStatuses,
            search: searchTerm
        }));
    }, [selectedStatuses, searchTerm]);


    const Dropdown = ({ name, options, label }: { name: string; options: any[]; label: string }) => (
        <div className="relative">
            <button
                onClick={() => handleDropdownClick(name)}
                className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
                style={{
                    minWidth: '120px',
                    maxWidth: '120px',
                }}
            >
                <span className="truncate mr-2">
                    {options.find(opt => opt.id === filters[name as keyof typeof filters])?.label || label}
                </span>
                {openDropdown === name ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
            </button>

            {openDropdown === name && (
                <>
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setOpenDropdown(null)}
                    />
                    <div className="absolute top-full left-0 mt-2 w-48 bg-[#2A2A2A] rounded-md shadow-lg z-20 py-1 border border-white/[0.08]">
                        <button
                            disabled
                            className="w-full text-left px-4 py-2 text-gray-500 bg-[#2A2A2A] cursor-default truncate opacity-50"
                        >
                            {label}
                        </button>
                        
                        <div className="border-b border-white/[0.08] my-1"></div>

                        {options.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => handleOptionSelect(name, option.label, option.id)}
                                className="w-full text-left px-4 py-2 text-gray-400 hover:bg-[#3a3a3a33] hover:text-white truncate"
                                title={option.label}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    const StatusButtons = () => (
        <div className="flex items-center gap-1 h-[32px]">
            {statuses.map(status => (
                <button
                    key={status.id}
                    onClick={() => handleStatusButtonClick(status.id)}
                    className={classNames(
                        'status-button',
                        status.id,
                        selectedStatuses.includes(status.id) ? 'active' : ''
                    )}
                >
                    {status.label}
                </button>
            ))}
        </div>
    );

    // Helper function to check if any filter is active
    const hasActiveFilters = () => {
        return (
            filters.status !== '' ||
            filters.customer !== '' ||
            filters.assignedTo !== '' ||
            filters.organization !== '' ||
            filters.startDate !== null ||
            filters.endDate !== null ||
            filters.statuses.length > 0 ||
            filters.search !== ''
        );
    };

    return (
        <>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search tickets..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-64 bg-[#2A2A2A] border border-white/[0.08] rounded-md pl-10 pr-4 py-2 text-white placeholder-gray-400"
                        />
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    </div>

                    <Dropdown
                        name="organization"
                        options={organizations}
                        label="Organization"
                    />

                    <div className="relative">
                        <button
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
                            style={{
                                minWidth: '120px',
                                maxWidth: '200px',
                            }}
                        >
                            <div className="flex items-center gap-2 truncate">
                                <FiCalendar className="flex-shrink-0" />
                                <span className="truncate">
                                    {(filters.startDate && filters.endDate)
                                        ? `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`
                                        : 'Duration'
                                    }
                                </span>
                            </div>
                        </button>

                        {showDatePicker && (
                            <>
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setShowDatePicker(false)}
                                />
                                <div className="absolute top-full left-0 mt-2 z-20 bg-[#2A2A2A] rounded-lg p-4 border border-white/[0.08]">
                                    <DatePicker
                                        selectsRange={true}
                                        startDate={filters.startDate}
                                        endDate={filters.endDate}
                                        onChange={(update: [Date | null, Date | null]) => {
                                            setFilters((prev: any) => ({
                                                ...prev,
                                                startDate: update[0],
                                                endDate: update[1]
                                            }));
                                            if (update[0] && update[1]) {
                                                setShowDatePicker(false);
                                            }
                                        }}
                                        inline
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <Dropdown
                        name="customer"
                        options={customer}
                        label="Customer"
                    />

                    <Dropdown
                        name="assignedTo"
                        options={assignees}
                        label="Assigned to"
                    />

                    <StatusButtons />

                    {hasActiveFilters() && (
                        <button
                            onClick={clearFilters}
                            className="text-red-400 hover:text-red-300"
                        >
                            Clear all
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2 ml-2">
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white hover:bg-[#3a3a3a33]"
                    >
                        <FiPlus /> Add ticket
                    </button>
                </div>
            </div>

            <TicketModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
            />
        </>
    );
} 